/**
 * Career Card Tooltip Configuration
 * This file contains tooltip text for various fields in the Career Card application
 */

export interface TooltipConfig {
  id: string;
  field: string;
  description: string;
}

export const careerCardTooltips: TooltipConfig[] = [
  {
    id: "role-readiness",
    field: "Role Readiness",
    description: "Select when you believe you'll be ready for the next role (e.g., Ready Now, 1–2 years, 2–5 years)."
  },
  {
    id: "strengths",
    field: "Strengths",
    description: "List two strengths that best represent your skills and capabilities."
  },
  {
    id: "areas-of-development",
    field: "Areas of Development",
    description: "Choose up to two areas where you want to grow or improve. "
  },
  {
    id: "previous-roles",
    field: "Previous Roles / Experiences",
    description: "Add your past roles with company name, job title, and years of experience. Approximate dates are fine."
  },
  {
    id: "location-preferences",
    field: "Location Preferences",
    description: "Select one or more preferred work locations. If you change preferences later, old selections will be removed."
  },
  {
    id: "ergs",
    field: "Employee Resource Groups (ERGs)",
    description: "Choose the ERGs you are a part of. If none, select 'N/A'."
  },
  {
    id: "communities",
    field: "Communities",
    description: "Add communities you are associated with. Example: TTEC Foundation. Enter N/A if none."
  },
  {
    id: "additional-roles",
    field: "Additional Roles / Interests",
    description: "Select any other roles you're interested in exploring beyond your current path."
  },
  {
    id: "import-excel",
    field: "Import / Upload Excel",
    description: "Admins can upload a career card Excel file. Please use the standard template to ensure data maps correctly."
  },
  {
    id: "export-options",
    field: "Export Options (Excel / PDF)",
    description: "Download your career card as Excel or PDF to share with your manager or for presentations."
  },
  {
    id: "calendar-fields",
    field: "Calendar Fields (Start/End Dates)",
    description: "Use the calendar to select start and end dates. If unsure, approximate dates are acceptable."
  },
  {
    id: "time-in-current-role",
    field: "Time in Current Role",
    description: "Enter the duration in your current role (e.g., '2 years', '18 months', '1.5 years'). Only letters, numbers, spaces, periods, and hyphens are allowed. Special characters like !@#$%^&*()_+[]{}| are not permitted."
  },
  {
    id: "certifications",
    field: "Certifications",
    description: "List your professional certifications separated by commas (e.g., 'AWS Certified, Microsoft Azure (2023), PMP'). Only letters, numbers, spaces, commas, periods, hyphens, parentheses, and forward slashes are allowed. Special characters like !@#$%^&*_+[]{}| are not permitted."
  }
];

/**
 * Helper function to get tooltip description by field ID
 * @param id The tooltip field ID
 * @returns The tooltip description or empty string if not found
 */
export const getTooltipById = (id: string): string => {
  const tooltip = careerCardTooltips.find(item => item.id === id);
  return tooltip ? tooltip.description : "";
};

/**
 * Helper function to get tooltip description by field name
 * @param fieldName The tooltip field name
 * @returns The tooltip description or empty string if not found
 */
export const getTooltipByField = (fieldName: string): string => {
  const tooltip = careerCardTooltips.find(item => item.field === fieldName);
  return tooltip ? tooltip.description : "";
};
